import { useEffect, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import {
  adminService,
  type GroupRoster,
  type OptionAdmin,
  type OptionGroupAdmin,
  type OptionGroupWrite,
  type OptionWrite,
  type RegistrationRow,
} from '../../services/adminService'
import { EmptyState, LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { confirmDelete } from '../confirm'

function toLocalInput(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  const off = d.getTimezoneOffset()
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16)
}

function fromLocalInput(v: string): string | null {
  return v ? new Date(v).toISOString() : null
}

export default function DinnersManager({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  const [groups, setGroups] = useState<OptionGroupAdmin[]>([])
  const [registrations, setRegistrations] = useState<RegistrationRow[]>([])
  const [loading, setLoading] = useState(true)
  const [groupDialog, setGroupDialog] = useState<{
    group: OptionGroupAdmin | null
  } | null>(null)

  const load = () =>
    Promise.all([
      adminService.listOptionGroups(eventId),
      adminService.listRegistrations(eventId),
    ])
      .then(([g, r]) => {
        setGroups(g)
        setRegistrations(r)
      })
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  const removeGroup = async (g: OptionGroupAdmin) => {
    if (!(await confirmDelete(`Delete group "${g.title}" and its options?`)))
      return
    try {
      await adminService.deleteOptionGroup(eventId, g.id)
      notify('Group deleted')
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const exportCsv = async () => {
    try {
      const blob = await adminService.exportRegistrations(eventId)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'registrations.csv'
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  if (loading) return <LoadingState />

  const registeredCount = registrations.filter(
    (r) => r.status === 'registered',
  ).length

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="body2" color="text.secondary">
          {registeredCount} registered · {groups.length} option group
          {groups.length === 1 ? '' : 's'}
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <Button
            variant="outlined"
            startIcon={<DownloadRoundedIcon />}
            onClick={exportCsv}
          >
            Export dinner roster
          </Button>
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            onClick={() => setGroupDialog({ group: null })}
          >
            Add group
          </Button>
        </Stack>
      </Stack>

      {groups.length === 0 ? (
        <EmptyState
          title="No option groups yet"
          description="Add a group (e.g. Dinners) so attendees can make a selection."
        />
      ) : (
        <Stack spacing={2}>
          {groups.map((g) => (
            <GroupCard
              key={g.id}
              eventId={eventId}
              group={g}
              registrations={registrations}
              onEdit={() => setGroupDialog({ group: g })}
              onDelete={() => removeGroup(g)}
              onChanged={load}
            />
          ))}
        </Stack>
      )}

      {groupDialog && (
        <GroupDialog
          eventId={eventId}
          group={groupDialog.group}
          count={groups.length}
          onClose={() => setGroupDialog(null)}
          onSaved={() => {
            setGroupDialog(null)
            load()
          }}
        />
      )}
    </Box>
  )
}

function GroupCard({
  eventId,
  group,
  registrations,
  onEdit,
  onDelete,
  onChanged,
}: {
  eventId: string
  group: OptionGroupAdmin
  registrations: RegistrationRow[]
  onEdit: () => void
  onDelete: () => void
  onChanged: () => void
}) {
  const { notify } = useToast()
  const [roster, setRoster] = useState<GroupRoster | null>(null)
  const [optionDialog, setOptionDialog] = useState<{
    option: OptionAdmin | null
  } | null>(null)

  const loadRoster = () =>
    adminService
      .getGroupRoster(eventId, group.id)
      .then(setRoster)
      .catch((e) => notify(apiError(e), 'error'))

  const removeOption = async (o: OptionAdmin) => {
    if (!(await confirmDelete(`Delete option "${o.title}"?`))) return
    try {
      await adminService.deleteOption(eventId, o.id)
      notify('Option deleted')
      onChanged()
      loadRoster()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const countFor = (optionId: string) =>
    roster?.options.find((o) => o.optionId === optionId)?.count ?? 0

  return (
    <Card variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {group.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {group.maxSelect === 1 ? 'Choose one' : `Choose up to ${group.maxSelect}`}
            {group.selectionDeadline
              ? ` · deadline ${new Date(group.selectionDeadline).toLocaleString()}`
              : ''}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onEdit}>
          <EditRoundedIcon />
        </IconButton>
        <IconButton size="small" color="error" onClick={onDelete}>
          <DeleteRoundedIcon />
        </IconButton>
      </Stack>

      <Divider sx={{ my: 1.5 }} />

      <Stack spacing={1}>
        {group.options.map((o) => (
          <Stack
            key={o.id}
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ pl: 1 }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body1" fontWeight={600} noWrap>
                {o.title}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={
                o.capacity != null
                  ? `${countFor(o.id)}/${o.capacity}`
                  : `${countFor(o.id)}`
              }
              sx={{ height: 22, fontWeight: 700 }}
            />
            <IconButton
              size="small"
              onClick={() => setOptionDialog({ option: o })}
            >
              <EditRoundedIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" color="error" onClick={() => removeOption(o)}>
              <DeleteRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>

      <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }}>
        <Button
          size="small"
          startIcon={<AddRoundedIcon />}
          onClick={() => setOptionDialog({ option: null })}
        >
          Add option
        </Button>
        <Button size="small" onClick={loadRoster}>
          {roster ? 'Refresh roster' : 'View roster'}
        </Button>
      </Stack>

      {roster && (
        <Accordion sx={{ mt: 1.5, boxShadow: 'none', border: '1px solid', borderColor: 'divider' }}>
          <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />}>
            <Typography variant="subtitle2" fontWeight={700}>
              Roster & overrides
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <RosterTable
              eventId={eventId}
              group={group}
              registrations={registrations}
              onChanged={() => {
                onChanged()
                loadRoster()
              }}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {optionDialog && (
        <OptionDialog
          eventId={eventId}
          groupId={group.id}
          option={optionDialog.option}
          count={group.options.length}
          onClose={() => setOptionDialog(null)}
          onSaved={() => {
            setOptionDialog(null)
            onChanged()
            loadRoster()
          }}
        />
      )}
    </Card>
  )
}

function RosterTable({
  eventId,
  group,
  registrations,
  onChanged,
}: {
  eventId: string
  group: OptionGroupAdmin
  registrations: RegistrationRow[]
  onChanged: () => void
}) {
  const { notify } = useToast()
  const [busy, setBusy] = useState<string | null>(null)

  const setSelection = async (attendeeId: string, optionId: string | null) => {
    setBusy(attendeeId)
    try {
      await adminService.setAttendeeSelection(
        eventId,
        attendeeId,
        group.id,
        optionId,
      )
      notify('Selection updated')
      onChanged()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setBusy(null)
    }
  }

  const registered = registrations.filter((r) => r.status === 'registered')
  if (registered.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No registered attendees yet.
      </Typography>
    )
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Attendee</TableCell>
          <TableCell>Selection (override)</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {registered.map((r) => (
          <TableRow key={r.attendeeId}>
            <TableCell>
              <Typography variant="body2" fontWeight={600}>
                {r.fullName || r.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {r.email}
              </Typography>
            </TableCell>
            <TableCell>
              <TextField
                select
                size="small"
                fullWidth
                disabled={busy === r.attendeeId}
                SelectProps={{ native: true }}
                defaultValue=""
                onChange={(e) =>
                  setSelection(r.attendeeId, e.target.value || null)
                }
              >
                <option value="">— clear —</option>
                {group.options.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.title}
                  </option>
                ))}
              </TextField>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function GroupDialog({
  eventId,
  group,
  count,
  onClose,
  onSaved,
}: {
  eventId: string
  group: OptionGroupAdmin | null
  count: number
  onClose: () => void
  onSaved: () => void
}) {
  const { notify } = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    title: group?.title ?? '',
    description: group?.description ?? '',
    maxSelect: group?.maxSelect ?? 1,
    requiresRegistration: group?.requiresRegistration ?? true,
    selectionDeadline: toLocalInput(group?.selectionDeadline),
  })
  const set = (k: keyof typeof form, v: string | boolean | number) =>
    setForm((f) => ({ ...f, [k]: v }))

  const save = async () => {
    if (!form.title) {
      notify('Title is required', 'error')
      return
    }
    const payload: OptionGroupWrite = {
      title: form.title,
      description: form.description || null,
      maxSelect: Number(form.maxSelect) || 1,
      minSelect: 0,
      requiresRegistration: form.requiresRegistration,
      selectionDeadline: fromLocalInput(form.selectionDeadline),
      ordinal: group?.ordinal ?? count,
    }
    setSaving(true)
    try {
      if (group) await adminService.updateOptionGroup(eventId, group.id, payload)
      else await adminService.createOptionGroup(eventId, payload)
      notify(group ? 'Group updated' : 'Group added')
      onSaved()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{group ? 'Edit group' : 'Add group'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Title"
            required
            fullWidth
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
          />
          <TextField
            label="Max selectable"
            type="number"
            fullWidth
            value={form.maxSelect}
            onChange={(e) => set('maxSelect', Number(e.target.value))}
            helperText="1 = attendees pick exactly one (e.g. dinners)"
          />
          <TextField
            label="Selection deadline"
            type="datetime-local"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.selectionDeadline}
            onChange={(e) => set('selectionDeadline', e.target.value)}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.requiresRegistration}
                onChange={(e) => set('requiresRegistration', e.target.checked)}
              />
            }
            label="Require event registration first"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function OptionDialog({
  eventId,
  groupId,
  option,
  count,
  onClose,
  onSaved,
}: {
  eventId: string
  groupId: string
  option: OptionAdmin | null
  count: number
  onClose: () => void
  onSaved: () => void
}) {
  const { notify } = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    title: option?.title ?? '',
    description: option?.description ?? '',
    location: option?.location ?? '',
    capacity: option?.capacity != null ? String(option.capacity) : '',
    startsAt: toLocalInput(option?.startsAt),
    endsAt: toLocalInput(option?.endsAt),
  })
  const set = (k: keyof typeof form, v: string) =>
    setForm((f) => ({ ...f, [k]: v }))

  const save = async () => {
    if (!form.title) {
      notify('Title is required', 'error')
      return
    }
    const payload: OptionWrite = {
      title: form.title,
      description: form.description || null,
      location: form.location || null,
      capacity: form.capacity === '' ? null : Number(form.capacity),
      startsAt: fromLocalInput(form.startsAt),
      endsAt: fromLocalInput(form.endsAt),
      ordinal: option?.ordinal ?? count,
    }
    setSaving(true)
    try {
      if (option) await adminService.updateOption(eventId, option.id, payload)
      else await adminService.createOption(eventId, groupId, payload)
      notify(option ? 'Option updated' : 'Option added')
      onSaved()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{option ? 'Edit option' : 'Add option'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Title"
            required
            fullWidth
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
          />
          <TextField
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
          />
          <TextField
            label="Location"
            fullWidth
            value={form.location}
            onChange={(e) => set('location', e.target.value)}
          />
          <TextField
            label="Capacity"
            type="number"
            fullWidth
            value={form.capacity}
            onChange={(e) => set('capacity', e.target.value)}
            helperText="Leave blank for unlimited"
          />
          <TextField
            label="Starts at"
            type="datetime-local"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.startsAt}
            onChange={(e) => set('startsAt', e.target.value)}
          />
          <TextField
            label="Ends at"
            type="datetime-local"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={form.endsAt}
            onChange={(e) => set('endsAt', e.target.value)}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
